import type React from "react";
import { cx } from "@emotion/css";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getExam } from "../../../hooks/useExam";
import { useKeyPress } from "../../../hooks/useKeyPress";
import {
	OutlinedPrimaryButton,
	PrimaryButton,
} from "../../Default/LekUI/Button";

interface ControllerProps {
	children?: React.ReactNode;
	isFirst: boolean;
	isLast: boolean;
	onNext: () => void;
	onPrev: () => void;
	currentId: string | number | null;
	itemsLength: number;
	className?: string;
}

export function Controller({
	children,
	isFirst,
	isLast,
	onNext,
	onPrev,
	currentId,
	itemsLength,
	className,
}: ControllerProps) {
	const { t } = useTranslation();

	return (
		<div className={cx("controller-container--sticky", className)}>
			{children}
			<div className="controller-container">
				<OutlinedPrimaryButton
					className="controller-button"
					startIcon={<NavigateBeforeIcon />}
					onClick={onPrev}
					disabled={isFirst}
					data-test-id="prev-question-button"
				>
					{t("commands.prev")}
				</OutlinedPrimaryButton>
				{currentId && (
					<div className="controller-counter" data-test-id="question-counter">
						<span className="size-14-light">
							{currentId} / {itemsLength}
						</span>
					</div>
				)}
				<PrimaryButton
					className="controller-button"
					endIcon={<NavigateNextIcon />}
					onClick={onNext}
					disabled={isLast}
					data-test-id="next-question-button"
				>
					{t("commands.next")}
				</PrimaryButton>
			</div>
		</div>
	);
}

interface QuestionControllerProps {
	onNext: () => void;
	onPrev: () => void;
	isFirstQuestion: boolean;
	isLastQuestion: boolean;
}

function QuestionController(props: QuestionControllerProps) {
	const params = useParams<{ id: string; questionId: string }>();
	const { questionId } = params;
	const exam = getExam(params.id);
	useKeyPress(["ArrowLeft", "ArrowRight"], (event: KeyboardEvent) => {
		if (event.key === "ArrowLeft") {
			props.onPrev();
		} else {
			props.onNext();
		}
	});

	return (
		<Controller
			className="pb-12"
			onPrev={props.onPrev}
			onNext={props.onNext}
			isFirst={props.isFirstQuestion}
			isLast={props.isLastQuestion}
			currentId={questionId}
			itemsLength={exam.questions.length}
		/>
	);
}

export default QuestionController;
